'use client';

import React, { useState } from 'react';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
// Custom components
// Assets
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { NavLink } from 'react-router-dom';
import Footer from '../../../components/footer/FooterAuth';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useLazyQuery } from '@apollo/client';
import { LOGIN } from '../../../graphql/query';

function Login() {
  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('brand.500', 'brand.400');
  const [show, setShow] = React.useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [login] = useLazyQuery(LOGIN, { fetchPolicy: 'network-only' });
  const handleClick = () => setShow(!show);
  const SignupSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Required'),
    password: Yup.string()
      .min(8, 'Password too short, insert a 8 characters password')
      .required('Required'),
  });

  async function SignIn(values: { email: string; password: string }) {
    const result = await login({
      variables: {
        input: {
          username: values.email,
          password: values.password,
        },
      },
    });
    return result;
  }

  return (
    <Flex position="relative" h="max-content">
      <Flex
        h={{
          sm: 'initial',
          md: 'unset',
          lg: '100vh',
          xl: '97vh',
        }}
        w="100%"
        mx="auto"
        pt={{ sm: '50px', md: '0px' }}
        px={{ lg: '30px', xl: '0px' }}
        ps={{ xl: '70px' }}
        alignItems="center"
        justifyContent="center"
        direction="column"
      >
        <NavLink
          to="/admin"
          style={() => ({
            width: 'fit-content',
            marginTop: '40px',
          })}
        ></NavLink>
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx={{ base: 'auto', lg: '0px' }}
          me="auto"
          h="100%"
          alignItems="start"
          justifyContent="center"
          mb={{ base: '30px', md: '60px' }}
          px={{ base: '25px', md: '0px' }}
          mt={{ base: '40px', md: '14vh' }}
          flexDirection="column"
        >
          <Box me="auto">
            <Heading color={textColor} fontSize="36px" mb="10px">
              Sign In
            </Heading>
            <Text
              mb="36px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              Enter your email and password to sign in!
            </Text>
          </Box>
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <Formik
              validationSchema={SignupSchema}
              initialValues={{
                email: '',
                password: '',
              }}
              onSubmit={(values) => {
                SignIn(values).then((result) => {
                  if (result.data) {
                    localStorage.setItem(
                      'token',
                      result.data.signIn.access_token
                    );
                    // history.push('/home');
                    window.location.href = `${
                      process.env.REACT_APP_URL_PREFIX || ''
                    }/home`;
                  } else if (result.error) {
                    setErrorMessage('Invalid credentials, please try again');
                  }
                });
              }}
            >
              {() => (
                <Form>
                  <VStack spacing={4} align="flex-start">
                    <Field
                      name="email"
                      isRequired={true}
                      variant="auth"
                      fontSize="sm"
                      ms={{ base: '0px', md: '0px' }}
                      type="email"
                      placeholder="mail@simmmple.com"
                      mb="24px"
                      fontWeight="500"
                      size="lg"
                      as={Input}
                      id="email"
                    >
                      {({ field, form }: any) => (
                        <FormControl
                          isInvalid={form.errors.email && form.touched.email}
                        >
                          <FormLabel display="flex">
                            Email{' '}
                            <Text ml={2} color={brandStars}>
                              *
                            </Text>
                          </FormLabel>
                          <Input
                            variant="auth"
                            fontSize="sm"
                            type="email"
                            mb="24px"
                            fontWeight="500"
                            size="lg"
                            as={'input'}
                            id="email"
                            name="email"
                            placeholder="mail@simmmple.com"
                            {...field}
                          />
                          <FormErrorMessage>
                            {form.errors.email}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="password">
                      {({ field, form }: any) => (
                        <FormControl
                          isInvalid={
                            form.errors.password && form.touched.password
                          }
                        >
                          <FormLabel display="flex">
                            Password{' '}
                            <Text ml={2} color={brandStars}>
                              *
                            </Text>
                          </FormLabel>

                          <InputGroup>
                            <Input
                              fontSize="sm"
                              mb="24px"
                              size="lg"
                              variant="auth"
                              as={Input}
                              id="password"
                              name="password"
                              placeholder="Min. 8 characters"
                              type={show ? 'text' : 'password'}
                              {...field}
                            />
                            <InputRightElement
                              display="flex"
                              alignItems="center"
                              mt="4px"
                            >
                              <Icon
                                color={textColorSecondary}
                                _hover={{ cursor: 'pointer' }}
                                as={
                                  show ? RiEyeCloseLine : MdOutlineRemoveRedEye
                                }
                                onClick={handleClick}
                              />
                            </InputRightElement>
                          </InputGroup>
                          <FormErrorMessage>
                            {form.errors.password}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    {errorMessage ? (
                      <div style={{ color: '#fc8181' }}>{errorMessage}</div>
                    ) : (
                      ''
                    )}
                    <Button
                      type="submit"
                      colorScheme="purple"
                      width="full"
                      fontSize="sm"
                      variant="brand"
                      fontWeight="500"
                      w="100%"
                      h="50"
                      mb="24px"
                    >
                      Submit
                    </Button>
                  </VStack>
                </Form>
              )}
            </Formik>
          </Flex>
        </Flex>
        <Footer />
      </Flex>
    </Flex>
  );
}

export default Login;
